import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import type { AdoptResult } from 'config-hub-server/src/service/adopt.js';
import { adoptMessage, type AdoptRequest } from '../domain/adopt.js';
import { useTRPCClient } from '../utils/trpc.js';
import { ENTITY_CALLS } from './entityCalls.js';

/**
 * Adopt: take what an environment holds into master.
 *
 * Offered from an environment's card, the matrix's "master is empty" state, the drawer's
 * Environments panel (one record) and a selection. `components/Adopt/AdoptConfirm.tsx`'s
 * `useConfirmedAdopt` puts every path behind ONE confirmation, because adopting is a write to
 * master — not to any environment.
 *
 * A record master has no copy of is copied in. A record master already has is left alone by the
 * whole-environment adopts (`all`, `studio-games` — they are the inbox's), and for records named
 * one by one keeps what that environment holds as the environment's OWN values
 * (`existing: 'environment'`): master's base, and so every other environment, never moves. The
 * base is replaced only by the CLI's `--overwrite`.
 */
export const useAdopt = () => {
    const client = useTRPCClient();

    const mutation = useMutation({
        mutationFn: (request: AdoptRequest) => {
            switch (request.scope) {
                case 'all':
                    return client.studios.adopt.mutate({ environment: request.environment });
                case 'records':
                    return ENTITY_CALLS[request.type].adopt(client, {
                        environment: request.environment,
                        keys: request.keys,
                        fields: request.fields,
                    });
                case 'studio-games':
                    return client.games.adopt.mutate({ environment: request.environment, studio: request.studio });
            }
        },
        onSuccess: (result: AdoptResult) => {
            const message = adoptMessage(result);
            notifications.show({ message: message.text, color: message.done ? 'green' : 'blue' });
        },
    });

    /** Requests to one environment, in order; the first failure stops the rest (the global toast has said why). */
    const adoptMany = async (requests: AdoptRequest[]): Promise<void> => {
        for (const request of requests) {
            try {
                await mutation.mutateAsync(request);
            } catch {
                return;
            }
        }
    };

    return { adoptMany, isPending: mutation.isPending };
};
